import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateScheduleRequest,
  ScheduleStatus,
  ScheduleSummary,
  TemplateSummary,
  UpdateScheduleRequest,
} from '@blesk/contracts';
import { apiGet, apiPatch, apiPost } from '../../shared/api';
import { usePagedList } from '../../shared/lists';
import { useProjectOptions, timezoneOf } from '../../shared/options';
import { SCHEDULE_STATUS_LABEL } from '../../shared/labels';
import {
  formatInProjectTz,
  isoToZonedInput,
  timezoneTitle,
  zonedInputToIso,
} from '../../shared/format';
import { useHasPermission, useSession } from '../../shared/session';
import { useUrlFilter } from '../../shared/filters';
import { PermissionGate } from '../shell/PermissionGate';
import { Dialog, EmptyRow, ErrorNote, Pager, RowMenu, type RowAction } from '../shell/ui';

/** §9.2, §13 ТЗ. Раздел «Расписания и напоминания». */
export function SchedulesPage() {
  return (
    <PermissionGate permission="surveys.read">
      <SchedulesInner />
    </PermissionGate>
  );
}

const GRANULARITY_LABEL: Record<string, string> = {
  DAY: 'День',
  WEEK: 'Неделя',
  MONTH: 'Месяц',
};

const WEEKDAY_LABEL: Record<string, string> = {
  MO: 'пн',
  TU: 'вт',
  WE: 'ср',
  TH: 'чт',
  FR: 'пт',
  SA: 'сб',
  SU: 'вс',
};

/**
 * §13: включить, приостановить, отключить. Отключённое расписание не удаляется
 * и не воскресает: новые запуски по нему не создаются, история остаётся.
 */
const STATUS_ACTIONS: Record<ScheduleStatus, ScheduleStatus[]> = {
  ACTIVE: ['PAUSED', 'DISABLED'],
  PAUSED: ['ACTIVE', 'DISABLED'],
  DISABLED: [],
};

/** Глагол действия, а не название состояния: в меню нажимают, а не читают. */
const STATUS_ACTION_LABEL: Record<ScheduleStatus, string> = {
  ACTIVE: 'Включить',
  PAUSED: 'Приостановить',
  DISABLED: 'Отключить',
};

/**
 * Правило по-русски: «FREQ=WEEKLY;BYDAY=MO» в таблице читает только тот,
 * кто его писал. Это показ, а не разбор: единственный настоящий разборщик
 * живёт на сервере (`rrule-lite`), он же решает, принимать правило или нет.
 * Формат, которого здесь нет, — `null`, и тогда показывается сырое правило.
 */
function calendarRuleLabel(rule: string): string | null {
  const parts = new Map<string, string>();
  for (const chunk of rule.trim().split(';')) {
    const eq = chunk.indexOf('=');
    if (eq > 0) parts.set(chunk.slice(0, eq).toUpperCase(), chunk.slice(eq + 1).toUpperCase());
  }

  switch (parts.get('FREQ')) {
    case 'DAILY':
      return 'ежедневно';
    case 'WEEKLY': {
      const days = (parts.get('BYDAY') ?? '')
        .split(',')
        .map((day) => WEEKDAY_LABEL[day.trim()])
        .filter((day): day is string => day !== undefined);
      if (days.length === 0) return null;
      const interval = Number(parts.get('INTERVAL') ?? '1');
      if (!Number.isInteger(interval) || interval < 1) return null;
      const every = interval === 1 ? 'еженедельно' : `раз в ${interval} недели`;
      return `${every} · ${days.join(', ')}`;
    }
    case 'MONTHLY': {
      const days = (parts.get('BYMONTHDAY') ?? '')
        .split(',')
        .map((day) => day.trim())
        .filter((day) => /^\d{1,2}$/.test(day));
      if (days.length === 0) return null;
      const last = days[days.length - 1] ?? '';
      const listed = days.length > 1 ? `${days.slice(0, -1).join(', ')} и ${last}` : last;
      return `${listed} числа`;
    }
    default:
      return null;
  }
}

/** «1 объект», «2 объекта», «5 объектов»: иначе подпись читается как машинная. */
function projectsCountLabel(count: number): string {
  const teens = count % 100;
  const ones = count % 10;
  if (teens >= 11 && teens <= 14) return `${count} объектов`;
  if (ones === 1) return `${count} объект`;
  if (ones >= 2 && ones <= 4) return `${count} объекта`;
  return `${count} объектов`;
}

/**
 * Теги групп с числом действующих объектов. Считаются по загруженному списку
 * объектов: тег живёт у объекта, отдельного справочника тегов нет и заводить
 * его ради выпадающего списка незачем.
 */
function useGroupTags(): Array<{ tag: string; count: number }> {
  const projects = useProjectOptions();
  const counts = new Map<string, number>();
  for (const project of projects.data ?? []) {
    if (!project.groupTag) continue;
    counts.set(project.groupTag, (counts.get(project.groupTag) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => a.tag.localeCompare(b.tag, 'ru'));
}

function SchedulesInner() {
  const session = useSession();
  const canWrite = useHasPermission('surveys.write');
  const queryClient = useQueryClient();

  const [projectId, setProjectId] = useUrlFilter('projectId');
  const [status, setStatus] = useUrlFilter('status');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduleSummary | null>(null);

  const projects = useProjectOptions();
  const groupTags = useGroupTags();
  const list = usePagedList<ScheduleSummary>('schedules', '/api/surveys/schedules', {
    projectId,
    status,
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['schedules'] });

  const setStatusMutation = useMutation({
    mutationFn: (input: { id: string; status: ScheduleStatus }) =>
      apiPost(`/api/surveys/schedules/${input.id}/status`, { status: input.status }),
    onSuccess: invalidate,
  });

  const zoneOf = (schedule: ScheduleSummary) =>
    timezoneOf(projects.data, schedule.projectId, session.defaultTimezone);

  /**
   * Пояс колонки «Запуск». Считает сервер (`nextRunTimezone`): у расписания
   * на объект это зона объекта, у группового — зона объекта, который стреляет
   * раньше всех. Пока расписание не стреляет, разворачивать нечего — остаётся
   * зона объекта или зона по умолчанию.
   */
  const runZoneOf = (schedule: ScheduleSummary) =>
    schedule.nextRunTimezone ?? zoneOf(schedule);

  /** Область группового расписания: тег без числа объектов ничего не говорит. */
  const scopeOf = (schedule: ScheduleSummary): string => {
    if (schedule.projectName) return schedule.projectName;
    if (!schedule.projectGroupTag) return '—';
    const count = groupTags.find((item) => item.tag === schedule.projectGroupTag)?.count ?? 0;
    return count === 0
      ? `группа ${schedule.projectGroupTag} · действующих объектов нет`
      : `группа ${schedule.projectGroupTag} · ${projectsCountLabel(count)}`;
  };

  return (
    <>
      <h1>Расписания и напоминания</h1>
      <p className="muted">
        Момент отправки считается в часовом поясе объекта; напоминаний не более двух.
      </p>

      <div className="toolbar">
        <label>
          Объект
          <select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
            <option value="">Все</option>
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Статус
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">Все</option>
            {Object.entries(SCHEDULE_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <span className="toolbar__spacer" />
        {canWrite && (
          <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
            Новое расписание
          </button>
        )}
      </div>

      <ErrorNote error={list.error ?? setStatusMutation.error} />

      <div className="table-wrap">
        <table className="table">
          <thead>
            {/* Слева то, по чему строку узнают и по чему решают: название, состояние,
                область. Широкие подробности — дальше, действия — в конце. */}
            <tr>
              <th>Название</th>
              <th>Область</th>
              {/* Правило и время — один факт «когда шлём», и раздельными
                  колонками они занимали 272 px. Пары ниже сведены по той же
                  причине: горизонтальная прокрутка прячет данные надёжнее,
                  чем две строки в ячейке (замер 18.08.2026). */}
              <th>Когда</th>
              <th>Запуск</th>
              <th>Анкета</th>
              <th>Окно и напоминания</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.items.length === 0 && <EmptyRow colSpan={7} loading={list.isPending} />}
            {list.items.map((schedule) => (
              <tr key={schedule.id}>
                <td>
                  {schedule.name}
                  <div className="cell-note">{SCHEDULE_STATUS_LABEL[schedule.status]}</div>
                </td>
                <td>{scopeOf(schedule)}</td>
                <td title={schedule.calendarRule}>
                  {calendarRuleLabel(schedule.calendarRule) ?? schedule.calendarRule}
                  <div className="cell-note">
                    {schedule.localTime} ·{' '}
                    {GRANULARITY_LABEL[schedule.periodGranularity] ?? schedule.periodGranularity}
                  </div>
                </td>
                <td
                  title={
                    schedule.projectGroupTag
                      ? 'Самый ранний запуск среди объектов группы'
                      : undefined
                  }
                >
                  След.: {formatInProjectTz(schedule.nextRunAt, runZoneOf(schedule))}
                  <div className="cell-note">
                    Посл.: {formatInProjectTz(schedule.lastRunAt, runZoneOf(schedule))}
                  </div>
                  {/* §8: у группового расписания объектов много, а колонка одна.
                      Момент показывается в поясе того объекта, который стреляет
                      раньше всех, и пояс подписан — иначе в строке стояли бы два
                      времени одного события (прогон QA 22.08.2026). */}
                  {schedule.projectGroupTag && (
                    <div className="cell-note">
                      в поясе {timezoneTitle(runZoneOf(schedule))}
                    </div>
                  )}
                </td>
                <td>
                  {schedule.templateName}
                  <div className="cell-note">в. {schedule.version}</div>
                </td>
                <td>
                  {schedule.responseWindowH} ч
                  <div className="cell-note">
                    {schedule.reminderOffsetsH.length === 0
                      ? 'без напоминаний'
                      : `напоминания: ${schedule.reminderOffsetsH.map((hours) => `${hours} ч`).join(', ')}`}
                  </div>
                </td>
                <td className="row-actions">
                  {canWrite && (
                    <RowMenu
                      actions={[
                        ...(schedule.status !== 'DISABLED'
                          ? [{ label: 'Изменить', onSelect: () => setEditing(schedule) }]
                          : []),
                        ...STATUS_ACTIONS[schedule.status].map<RowAction>((next) => ({
                          label: STATUS_ACTION_LABEL[next],
                          danger: next === 'DISABLED',
                          onSelect: () => {
                            if (
                              next === 'DISABLED' &&
                              !confirm(
                                `Отключить расписание «${schedule.name}»? Новые запуски создаваться не будут.`,
                              )
                            ) {
                              return;
                            }
                            setStatusMutation.mutate({ id: schedule.id, status: next });
                          },
                        })),
                      ]}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager page={list.page} perPage={list.perPage} total={list.total} onPage={list.setPage} />

      {createOpen && <CreateDialog onClose={() => setCreateOpen(false)} onSaved={invalidate} />}
      {editing && (
        <EditDialog schedule={editing} onClose={() => setEditing(null)} onSaved={invalidate} />
      )}
    </>
  );
}

/** Опубликованные версии анкет: расписание нельзя повесить на черновик (§9.1). */
function usePublishedVersions() {
  return useQuery({
    queryKey: ['surveys', 'templates'],
    queryFn: () => apiGet<TemplateSummary[]>('/api/surveys/templates'),
    select: (templates) =>
      templates
        .filter((template) => !template.archivedAt)
        .flatMap((template) =>
          template.versions
            .filter((version) => version.status === 'PUBLISHED')
            .map((version) => ({
              id: version.id,
              label: `${template.name} · версия ${version.version}`,
            })),
        ),
  });
}

const RULE_EXAMPLES: ReadonlyArray<{ rule: string; meaning: string }> = [
  { rule: 'FREQ=DAILY', meaning: 'ежедневно' },
  { rule: 'FREQ=WEEKLY;BYDAY=MO,FR', meaning: 'по понедельникам и пятницам' },
  { rule: 'FREQ=WEEKLY;INTERVAL=2;BYDAY=MO', meaning: 'раз в 2 недели по понедельникам' },
  { rule: 'FREQ=MONTHLY;BYMONTHDAY=1,15', meaning: '1 и 15 числа' },
];

/**
 * Подпись к границам действия расписания.
 *
 * У расписания на объект граница вводится в зоне этого объекта. У группового
 * объектов много и зона у каждого своя, поэтому граница вводится в зоне по
 * умолчанию, а время рассылки планировщик разворачивает в зоне каждого объекта
 * группы — так и работает §9.2.
 */
function validityHint(scope: 'project' | 'group', zone: string): string {
  return scope === 'project'
    ? `По времени объекта (${zone})`
    : `По времени ${zone}. Само время рассылки считается в зоне каждого объекта группы`;
}

function CreateDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const session = useSession();
  const projects = useProjectOptions();
  const groupTags = useGroupTags();
  const versions = usePublishedVersions();

  const [name, setName] = useState('');
  const [scope, setScope] = useState<'project' | 'group'>('project');
  const [projectId, setProjectId] = useState('');
  const [projectGroupTag, setProjectGroupTag] = useState('');
  const [versionId, setVersionId] = useState('');
  // Значения заказчика по умолчанию (D3–D5): раз в 2 недели, 52 ч на ответ,
  // напоминания с интервалом 24 ч
  const [calendarRule, setCalendarRule] = useState('FREQ=WEEKLY;INTERVAL=2;BYDAY=MO');
  const [localTime, setLocalTime] = useState('10:00');
  const [periodGranularity, setPeriodGranularity] =
    useState<CreateScheduleRequest['periodGranularity']>('WEEK');
  const [responseWindowH, setResponseWindowH] = useState(52);
  const [reminders, setReminders] = useState('24, 48');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');

  // Границы действия вводятся в той же зоне, в которой раздел показывает время:
  // у расписания на объект — в зоне объекта, у группового — в зоне по умолчанию
  const zone =
    scope === 'project'
      ? timezoneOf(projects.data, projectId || null, session.defaultTimezone)
      : session.defaultTimezone;

  const save = useMutation({
    mutationFn: (body: CreateScheduleRequest) => apiPost('/api/surveys/schedules', body),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  // Форма длинная: случайный Esc или клик мимо не должен стирать введённое
  return (
    <Dialog
      title="Новое расписание"
      onClose={onClose}
      confirmClose="Закрыть форму? Введённое расписание не сохранится."
    >
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate({
            name,
            // §9.2: ровно одно из двух — объект или тег группы
            ...(scope === 'project' ? { projectId } : { projectGroupTag }),
            versionId,
            calendarRule,
            localTime,
            periodGranularity,
            responseWindowH,
            reminderOffsetsH: parseReminders(reminders),
            startsAt: zonedInputToIso(startsAt, zone),
            ...(endsAt ? { endsAt: zonedInputToIso(endsAt, zone) } : {}),
          });
        }}
      >
        <label>
          Название
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            minLength={3}
          />
        </label>

        <label>
          Область
          <select
            value={scope}
            onChange={(event) => setScope(event.target.value as 'project' | 'group')}
          >
            <option value="project">Один объект</option>
            <option value="group">Тег группы объектов</option>
          </select>
        </label>

        {scope === 'project' ? (
          <label>
            Объект
            <select
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
              required
            >
              <option value="">Выберите объект</option>
              {(projects.data ?? []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label>
            Тег группы
            {/* Выбор, а не свободный ввод: опечатка в теге давала расписание,
                которое молча не охватывает ни одного объекта */}
            <select
              value={projectGroupTag}
              onChange={(event) => setProjectGroupTag(event.target.value)}
              required
            >
              <option value="">Выберите тег</option>
              {groupTags.map((item) => (
                <option key={item.tag} value={item.tag}>
                  {item.tag} · {projectsCountLabel(item.count)}
                </option>
              ))}
            </select>
            {groupTags.length === 0 && (
              <span className="muted">
                Ни у одного действующего объекта нет тега группы. Проставьте тег в разделе «Объекты»
                или заведите расписание на один объект.
              </span>
            )}
          </label>
        )}

        <label>
          Версия анкеты
          <select value={versionId} onChange={(event) => setVersionId(event.target.value)} required>
            <option value="">Выберите опубликованную версию</option>
            {(versions.data ?? []).map((version) => (
              <option key={version.id} value={version.id}>
                {version.label}
              </option>
            ))}
          </select>
        </label>
        {versions.data?.length === 0 && (
          <p className="muted">Опубликованных версий нет — сначала опубликуйте анкету.</p>
        )}

        <ScheduleFields
          calendarRule={calendarRule}
          setCalendarRule={setCalendarRule}
          localTime={localTime}
          setLocalTime={setLocalTime}
          periodGranularity={periodGranularity}
          setPeriodGranularity={setPeriodGranularity}
          responseWindowH={responseWindowH}
          setResponseWindowH={setResponseWindowH}
          reminders={reminders}
          setReminders={setReminders}
        />

        <label>
          Действует с
          <input
            type="datetime-local"
            value={startsAt}
            onChange={(event) => setStartsAt(event.target.value)}
            required
          />
          <span className="muted">{validityHint(scope, zone)}</span>
        </label>
        <label>
          Действует по (необязательно)
          <input
            type="datetime-local"
            value={endsAt}
            min={startsAt || undefined}
            onChange={(event) => setEndsAt(event.target.value)}
          />
          <span className="muted">{validityHint(scope, zone)}</span>
        </label>

        <ErrorNote error={save.error} />
        <div className="form__actions">
          <button type="button" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            Создать
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Правка расписания. Объект, тег группы и версия анкеты не меняются:
 * это была бы подмена смысла уже созданных запусков (§9.2) — заводится новое.
 * Начало действия тоже неизменно: по нему уже прошли рассылки.
 */
function EditDialog({
  schedule,
  onClose,
  onSaved,
}: {
  schedule: ScheduleSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  const session = useSession();
  const projects = useProjectOptions();

  const zone = timezoneOf(projects.data, schedule.projectId, session.defaultTimezone);
  const scope: 'project' | 'group' = schedule.projectId ? 'project' : 'group';

  const [name, setName] = useState(schedule.name);
  const [calendarRule, setCalendarRule] = useState(schedule.calendarRule);
  const [localTime, setLocalTime] = useState(schedule.localTime);
  const [periodGranularity, setPeriodGranularity] = useState(
    schedule.periodGranularity as CreateScheduleRequest['periodGranularity'],
  );
  const [responseWindowH, setResponseWindowH] = useState(schedule.responseWindowH);
  const [reminders, setReminders] = useState(schedule.reminderOffsetsH.join(', '));
  // null — поле ещё не трогали: пока так, значение пересчитывается из зоны,
  // а она известна только после загрузки списка объектов
  const [editedEndsAt, setEditedEndsAt] = useState<string | null>(null);
  const endsAt = editedEndsAt ?? isoToZonedInput(schedule.endsAt, zone);

  const save = useMutation({
    mutationFn: (body: UpdateScheduleRequest) =>
      apiPatch(`/api/surveys/schedules/${schedule.id}`, body),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Dialog
      title={`Расписание «${schedule.name}»`}
      onClose={onClose}
      confirmClose="Закрыть форму? Несохранённые изменения пропадут."
    >
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate({
            name,
            calendarRule,
            localTime,
            periodGranularity,
            responseWindowH,
            reminderOffsetsH: parseReminders(reminders),
            // Пустое поле — снятая граница, а не «оставить как было»
            endsAt: endsAt ? zonedInputToIso(endsAt, zone) : null,
          });
        }}
      >
        <p className="muted">
          {schedule.projectName ?? `группа: ${schedule.projectGroupTag}`} · {schedule.templateName}{' '}
          версия {schedule.version} · действует с {formatInProjectTz(schedule.startsAt, zone)}
        </p>

        <label>
          Название
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            minLength={3}
          />
        </label>

        <ScheduleFields
          calendarRule={calendarRule}
          setCalendarRule={setCalendarRule}
          localTime={localTime}
          setLocalTime={setLocalTime}
          periodGranularity={periodGranularity}
          setPeriodGranularity={setPeriodGranularity}
          responseWindowH={responseWindowH}
          setResponseWindowH={setResponseWindowH}
          reminders={reminders}
          setReminders={setReminders}
        />

        <label>
          Действует по (пусто — бессрочно)
          <input
            type="datetime-local"
            value={endsAt}
            min={isoToZonedInput(schedule.startsAt, zone)}
            onChange={(event) => setEditedEndsAt(event.target.value)}
          />
          <span className="muted">{validityHint(scope, zone)}</span>
        </label>

        <ErrorNote error={save.error} />
        <div className="form__actions">
          <button type="button" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            Сохранить
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function ScheduleFields(props: {
  calendarRule: string;
  setCalendarRule: (value: string) => void;
  localTime: string;
  setLocalTime: (value: string) => void;
  periodGranularity: CreateScheduleRequest['periodGranularity'];
  setPeriodGranularity: (value: CreateScheduleRequest['periodGranularity']) => void;
  responseWindowH: number;
  setResponseWindowH: (value: number) => void;
  reminders: string;
  setReminders: (value: string) => void;
}) {
  const preview = calendarRuleLabel(props.calendarRule);
  return (
    <>
      <label>
        Календарное правило в формате RRULE
        <input
          value={props.calendarRule}
          onChange={(event) => props.setCalendarRule(event.target.value)}
          required
        />
        {/* Правило вводится руками, поэтому рядом с полем видно, как его
            прочитает планировщик: опечатку в BYDAY иначе не заметить */}
        <span className="muted">
          {preview
            ? `Прочитается как: ${preview}`
            : 'Формат не распознан — правило проверит сервер'}
        </span>
        <span className="muted">
          {RULE_EXAMPLES.map((example) => `${example.rule} — ${example.meaning}`).join(' · ')}
        </span>
      </label>

      <label>
        Время по объекту
        <input
          type="time"
          value={props.localTime}
          onChange={(event) => props.setLocalTime(event.target.value)}
          required
        />
      </label>

      <label>
        Период учёта
        <select
          value={props.periodGranularity}
          onChange={(event) =>
            props.setPeriodGranularity(
              event.target.value as CreateScheduleRequest['periodGranularity'],
            )
          }
        >
          {Object.entries(GRANULARITY_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <label>
        Окно ответа, часов
        <input
          type="number"
          min={1}
          max={720}
          value={props.responseWindowH}
          onChange={(event) => props.setResponseWindowH(Number(event.target.value))}
          required
        />
      </label>

      <label>
        Напоминания через, часов
        <input
          value={props.reminders}
          onChange={(event) => props.setReminders(event.target.value)}
          placeholder="24, 48"
        />
        <span className="muted">
          Не более двух, по возрастанию, раньше окна ответа. Пусто — без напоминаний.
        </span>
      </label>
    </>
  );
}

/** «24, 40» → [24, 40]. Разбор мягкий, отказ даёт сервер своей схемой. */
function parseReminders(raw: string): number[] {
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .map(Number)
    .filter((value) => Number.isFinite(value));
}
